import { useCallback, useEffect, useState } from "react";
import { listAnalyses, deleteAnalysis, type AnalysisRecord } from "../services/api";
import { useLang } from "../hooks/useLang";
import Icon from "./icons";

type Props = {
  /** Tıklandığında parent input'a doldurup analizi tekrar çalıştırmak için. */
  onReplay: (question: string) => void;
  /** Yeni analiz tamamlandığında parent listeyi yenilemek için
   * bu component'in re-fetch etmesini ister (key prop ile). */
  refreshToken?: number;
};

export default function RecentAnalyses({ onReplay, refreshToken = 0 }: Props) {
  const { t, lang } = useLang();
  const [items, setItems] = useState<AnalysisRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await listAnalyses(15);
      setItems(r.analyses || []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh, refreshToken]);

  const onDelete = useCallback(async (id: string) => {
    if (!confirm(t("recent_delete_confirm"))) return;
    try {
      await deleteAnalysis(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [t]);

  if (items.length === 0 && !loading && !err) return null;

  return (
    <details
      className="recent-analyses"
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary>
        <strong>{t("recent_queries")}</strong>
        <span className="hint" style={{ marginLeft: 8 }}>
          {loading ? t("recent_loading") : items.length > 0 ? `${items.length} ${t("recent_count_one")}` : t("recent_empty")}
        </span>
      </summary>
      {err && <div className="error">{t("err_prefix")}: {err}</div>}
      {items.length > 0 && (
        <ul className="ra-list">
          {items.map((a) => {
            const when = a.created_at ? new Date(a.created_at).toLocaleString(lang === "en" ? "en-GB" : "tr-TR") : "";
            return (
              <li key={a.id} className="ra-row">
                <button
                  className="ra-item"
                  onClick={() => {
                    onReplay(a.question);
                    setOpen(false); // soru secilince listeyi kapat — cevap icin scroll gerekmesin
                  }}
                  title={t("recent_replay_hint")}
                >
                  <span className="ra-q">{a.question}</span>
                  <span className="ra-meta">
                    {a.chunks_used} {t("recent_sources_unit")} · {Math.round(a.usage_ms)}ms · {when}
                  </span>
                </button>
                <button className="ra-del" onClick={() => onDelete(a.id)} title={t("recent_delete_btn_title")} aria-label={t("delete")}><Icon name="x" size={14} /></button>
              </li>
            );
          })}
        </ul>
      )}
    </details>
  );
}
